import { Grid, Radio, Tooltip, Typography, createStyles, makeStyles } from '@material-ui/core';
import { ArrowForward, Cancel, CheckCircle, Error, VerifiedUser, WatchLater } from '@material-ui/icons';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {
  DataGridPro,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridCellParams,
  GridFilterModel,
  GridSortModel,
  GridToolbarContainer,
  GridValueGetterParams,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { errorMessages } from 'common/errorMessages.strings';
import { funcaoParticipanteDescriptions, getParticipanteShortDescription, statusFormularioDescriptions, tipoAcessoParticipanteDescriptions } from 'common/genericDescriptions';
import { ParticipanteResponse, ParticipantesFilter, Perfil, RegistroStatus } from 'common/genericTypes';
import { ParticipanteListItem } from 'components/participante/participanteListItem';
import { StatusToast } from 'components/validation/statusToast';
import { ValidationDrawer, ValidationRequestKind } from 'components/validation/validationDrawer';
import { Button, MdSpace, TextButton } from 'm2m.components';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { RootState } from 'store';
import { addAlert } from 'store/modules/snackBar';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { componentsPropsValues, rowsPerPageOptionsValues, useStyles } from 'utils/dataGrid/dataGridUtils';
import { localizedTextsMap } from 'utils/dataGridUtils';
import { addCpfCnpjMask } from 'utils/mask';

const useStylesTexto = makeStyles(() =>
  createStyles({
    wrapTxt: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  })
);

const Participantes: React.FC = () => {
  const postParticipantesPaginated = useSelector((state: RootState) => state.participanteApi.postParticipantePaginated);
  const postValidateParticipante = useSelector((state: RootState) => state.participanteApi.postValidateParticipante);
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);

  const dispatch = useDispatch();
  const history = useHistory();

  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(1);
  const style = useStylesTexto();

  const [showLoader, setShowLoader] = useState<boolean>(true);

  const [participantes, setParticipantes] = useState<ParticipanteResponse[]>([]);
  const [openValidationDrawer, setOpenValidationDrawer] = useState<boolean>(false);
  const [checked, setChecked] = useState<string>();
  const [checkedStatus, setCheckedStatus] = useState<RegistroStatus>();
  const [reject, setReject] = useState<boolean>(false);
  const [lastParticipanteValidated, setLastParticipanteValidated] = useState<string>();
  const [checkedParticipanteId, setCheckedParticipanteId] = useState<string>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(20);
  const [participantesFilter, setParticipanteFilter] = useState<Array<ParticipantesFilter>>([]);
  const [linkOperator, setLinkOperator] = useState<any>('AND');


  const getParticipantes = useCallback(async () => {
    try {
      setShowLoader(true);
      const result = await postParticipantesPaginated(
        dispatch,
        { linkOperator: linkOperator, page: page + 1, pageSize, FilterTypeParticipante: participantesFilter },
        true
      );
      setParticipantes(result.result);
      setPageCount(result.totalCount);
      setShowLoader(false);
    } catch (error) {}
  }, [dispatch, postParticipantesPaginated,participantesFilter,pageSize, page, linkOperator]);

  const handleValidation = async (status: RegistroStatus, justification?: string, attachments?: File[]) => {
    if (!checkedParticipanteId) {
      return;
    }
    try {
      setSubmitting(true);
      switch (status) {
        case RegistroStatus.Aprovado:
          await postValidateParticipante(dispatch, status, checkedParticipanteId, justification, attachments, true);
          break;
        case RegistroStatus.Reprovado:
        case RegistroStatus.AguardandoCorrecao:
          if (!justification && !attachments?.length) {
            dispatch(addAlert({ severity: 'error', message: errorMessages.validation.requiredFields }));
            return;
          }
          await postValidateParticipante(dispatch, status, checkedParticipanteId, justification, attachments, true);
      }
      const updatedParticipantes = participantes.map((participante) => {
        if (participante.id === checkedParticipanteId) {
          participante.registro.status = status;
        }
        return participante;
      });
      setParticipantes(updatedParticipantes);
      setCheckedStatus(status);
      setLastParticipanteValidated(checked);
      setOpenValidationDrawer(false);
      setChecked(undefined);
    } catch (error) {
      console.log(error)
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidationClick = (reject: boolean) => () => {
    setReject(reject);
    setOpenValidationDrawer(true);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  useEffect(() => {
    getParticipantes();
  }, [getParticipantes, page, participantesFilter]);

  const onFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!filterModel.items) {
        setParticipanteFilter([]);
        return;
      }
      setLinkOperator(filterModel?.linkOperator?.toString()?.toUpperCase() ?? 'AND');
      var participanteFilter = new Array<ParticipantesFilter>();

      filterModel.items.forEach((filter) => {
        let operatorValue =
          filter.operatorValue == 'startsWith'
            ? 'STARTS_WITH'
            : filter.operatorValue == 'endsWith'
            ? 'ENDS_WITH'
            : filter.operatorValue?.toUpperCase();

        let formattedValue = filter.value?.toString();
        if (formattedValue) {
         let params = {
            columnType: filter.columnField.toUpperCase(),
            operationFilterType: operatorValue,
            value: formattedValue,
          } as ParticipantesFilter;

          participanteFilter.push(params);
        }
      });

      setParticipanteFilter(participanteFilter);
    },
    [getParticipantes, page, pageSize, linkOperator, participantesFilter]
  );

  const onSortChange = useCallback(
    (filterModel: GridSortModel) => {
      if (filterModel == null) return;

      var filter = filterModel[0];
      let params = {
        columnType: filter.field.toUpperCase(),
        orderByType: filter.sort?.toUpperCase(),
      } as ParticipantesFilter;

      var newEFFilter = participantesFilter.filter(
        (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC'
      ) as Array<ParticipantesFilter>;
      newEFFilter.push(params);
      setParticipanteFilter([...newEFFilter]);
    },
    [getParticipantes, page, pageSize, participantesFilter]
  );

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains', 'startsWith', 'endsWith'].includes(value)
  );

  const getStatusIcon = (status: RegistroStatus) => {
    const classes = useStyles(status as RegistroStatus)();
    switch (status) {
      case RegistroStatus.EmAnalise:
        return <WatchLater fontSize="small" className={classes.icon} />;
      case RegistroStatus.Aprovado:
        return <CheckCircle fontSize="small" className={classes.icon} />;
      case RegistroStatus.Reprovado:
        return <Cancel fontSize="small" className={classes.icon} />;
      case RegistroStatus.AguardandoCorrecao:
        return <Error fontSize="small" className={classes.icon} />;
      case RegistroStatus.Excluido:
        return <Cancel fontSize="small" className={classes.icon} />;
      case RegistroStatus.AguardandoAprovacaoEdicao:
        return <WatchLater fontSize="small" className={classes.icon} />;
      case RegistroStatus.AguardandoAprovacaoAditamento:
        return <WatchLater fontSize="small" className={classes.icon} />;
    }
  };

  const onChangeRadio = (participante: ParticipanteResponse) => {
    setChecked(participante.ticket);
    setCheckedParticipanteId(participante.id);
  }

  const filterOperatorsSelect = getGridSingleSelectOperators().filter(({value}) => 
  ['is', 'not'].includes(value));

  const getGridColumns = useCallback(
    () => [
      {
        field: 'status',
        headerName: 'STATUS',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: true,
        width: 100,
        minWidth: 100,
        maxWidth: 150,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).ticket,
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <>
             <Radio
                  checked={checked === participante.ticket}
                  onChange={(event) => {
                    onChangeRadio(participante)
                  }}
                  value={participante.ticket}
                  color="primary"
                  disabled={participante.registro.status !== RegistroStatus.EmAnalise}
                />
               <Tooltip
                title={statusFormularioDescriptions[participante.registro.status]}
                aria-label={statusFormularioDescriptions[participante.registro.status]}
              >
                {getStatusIcon(participante.registro.status)}
              </Tooltip>
            </>
          );
        },
      },    
      {
        field: 'ticket',
        headerName: 'Ticket',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).ticket.toString(),
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <Typography className={style.wrapTxt} variant="body2">
            {participante.ticket}
          </Typography>
          );
        },
      },
      {
        field: 'avaliadoPeloKyc',
        headerName: 'CAF',
        type: "singleSelect",
        valueOptions: ["Avaliado"],
        filterOperators: filterOperatorsSelect,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 75,
        minWidth: 75,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).avaliadoPeloKyc,
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <>
             {participante.avaliadoPeloKyc && (
                <Tooltip title={'Avaliado pelo CAF'} aria-label={'Avaliado pelo CAF'}>
                  {<VerifiedUser fontSize="small" color="action" />}
                </Tooltip>
              )}
            </>
          );
        },
      },
      {
        field: 'cnpj',
        headerName: 'CNPJ',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 200,
        maxWidth: 250,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).cnpj.toString(),
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <Typography variant="body2">
            {addCpfCnpjMask(participante.cnpj)}
          </Typography>
          );
        },
      },
      {
        field: 'SUSPENSO',
        headerName: 'Suspenso',
        type: "singleSelect",
        valueOptions: ["Suspenso"],
        filterOperators: filterOperatorsSelect,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 200,
        maxWidth: 250,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).suspenso,
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <Typography variant="body2">
             {participante.suspenso? 'Suspenso': 'Ativo'}
          </Typography>
          );
        },
      },
      {
        field: 'RAZAO_SOCIAL',
        headerName: 'Razão Social',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 250,
        minWidth: 200,
        maxWidth: 400,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).razaoSocial,
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <Typography className={style.wrapTxt} variant="body2">
            {participante.razaoSocial}
          </Typography>
          );
        },
      },
      {
        field: 'ATIVIDADE_PRINCIPAL_DESENVOLVIDA',
        headerName: 'Principal Atividade Desenvolvida',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 250,
        minWidth: 200,
        maxWidth: 400,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).atividadePrincipalDesenvolvida,
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <Typography variant="body2">{participante.atividadePrincipalDesenvolvida}</Typography>
          );
        },
      },
      {
        field: 'FUNCAO',
        headerName: 'Funções do Participante',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 250,
        minWidth: 200,
        maxWidth: 400,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).funcoes.map(pf => funcaoParticipanteDescriptions[pf.funcao]).join(', ').toString(),
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <Typography variant="body2">
            {participante.funcoes.map((pf) => funcaoParticipanteDescriptions[pf.funcao]).join(', ')}
          </Typography>
          );
        },
      },
      {
        field: 'FATURAMENTO_MEDIOMENSAL_ULTIMOS_DOZE_MESES',
        headerName: 'Faturamento Médio Mensal',
        type: 'string',
        filterable: false,
        sortable: false,
        disableExport: false,
        width: 250,
        minWidth: 200,
        maxWidth: 400,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).faturamentoMedioMensalUltimosDozeMeses,
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <Typography variant="body2">
             <Typography variant="body2">{participante.faturamentoMedioMensalUltimosDozeMeses}</Typography>
          </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName:' ',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: true,
        width: 250,
        minWidth: 200,
        maxWidth: 400,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse),
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <TextButton onClick={() => history.push(`/gerenciar/participantes/detalhes/${participante.id}`)}>
            {'Mais Detalhes'}
            <ArrowForward style={{ marginLeft: 10 }} fontSize={'small'} />
          </TextButton>
          );
        },
      },     
      
    ],[checked, checkedParticipanteId]
  );

  return (
    <div>   
      <MdSpace />
      {!isUsuarioLeitura && (
          <Grid container spacing={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={handleValidationClick(false)}
                disabled={!checked}
              >
                <CheckCircleIcon fontSize="small" style={{ marginRight: '10px' }} />
                {'Aprovar'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                size="small"
                color="error"
                onClick={handleValidationClick(true)}
                disabled={!checked}
              >
                <CheckCircleIcon fontSize="small" style={{ marginRight: '10px' }} />
                {'Rejeitar'}
              </Button>
            </Grid>
          </Grid>
        )}
      <MdSpace />
      {checkedStatus && (
                <>
                  <StatusToast
                    status={checkedStatus}
                    ticket={lastParticipanteValidated ?? ''}
                    onDismiss={() => setCheckedStatus(undefined)}
                  />
                  <MdSpace />
                </>
              )}
      <DataGridPro
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={componentsPropsValues}
        rows={participantes}
        columns={getGridColumns()}
        components={{
          Toolbar: CustomToolBarUtils,
        }}
        disableSelectionOnClick
        rowsPerPageOptions={rowsPerPageOptionsValues}
        page={page}
        rowCount={pageCount}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize), setPage(0);
        }}
        pageSize={pageSize}
        loading={showLoader}
        filterMode="server"
        sortingMode="server"
        onFilterModelChange={onFilterChange}
        onSortModelChange={onSortChange}
      />

    <div style={{ marginRight: 40, paddingTop: 40 }}>
      <ValidationDrawer
        kind={ValidationRequestKind.PARTICIPANTE}
        ticket={checked}
        opened={openValidationDrawer}
        onClose={() => setOpenValidationDrawer(false)}
        reject={reject}
        onSubmit={handleValidation}
        disableButtons={submitting}
      />
    </div>
    </div>
  );
};

export default Participantes;
